import React from 'react';

/**
 * OnboardingModal - Quick Profile Setup flow (3 steps)
 * Step 1: Goal Selection + Units
 * Step 2: Profile Details (Age, Sex, Height, Weight, Activity)
 * Step 3: Confirmation (directs to SmartGoals for health conditions)
 */
const OnboardingModal = ({
  show,
  onClose,
  onboardingStep,
  setOnboardingStep,
  onboardingData,
  setOnboardingData,
  userId,
  BACKEND_URL,
  setUseImperial,
  loadUserProfile,
  loadAiMiq,
  loadAiNutritionGoals
}) => {
  if (!show) return null;
  
  // Generate height options for imperial (feet and inches dropdowns)
  const feetOptions = [4, 5, 6, 7];
  const inchesOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  
  const handleComplete = async () => {
    // Validate required fields first
    if (!userId) {
      alert('Session expired. Please log in again.');
      return;
    }
    
    if (!onboardingData.gender) {
      alert('Please select your sex.');
      return;
    }
    
    if (!onboardingData.age || parseInt(onboardingData.age) < 10) {
      alert('Please enter a valid age.');
      return;
    }
    
    if (!onboardingData.weight || parseFloat(onboardingData.weight) < 20) {
      alert('Please enter a valid weight.');
      return;
    }
    
    // Save profile
    try {
      let goalWeightKg = null;
      if (onboardingData.goalWeight) {
        goalWeightKg = onboardingData.useImperial 
          ? parseFloat(onboardingData.goalWeight) * 0.453592
          : parseFloat(onboardingData.goalWeight);
      }
      
      const profileData = {
        user_id: userId,
        height_cm: parseFloat(onboardingData.height) || 170,
        weight_kg: parseFloat(onboardingData.weight) || 70,
        age: parseInt(onboardingData.age) || 30,
        gender: onboardingData.gender || 'other',
        activity_level: onboardingData.activityLevel || 'moderate',
        goal_weight_kg: goalWeightKg,
        use_imperial: onboardingData.useImperial || false
      };
      
      console.log('Saving profile:', profileData);
      
      const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        setUseImperial(onboardingData.useImperial);
        await loadUserProfile(userId);
        
        // Load AI Nutrition Goals (may have default targets based on profile)
        if (loadAiNutritionGoals) await loadAiNutritionGoals(userId);
        
        await loadAiMiq(userId);
        onClose();
        setOnboardingStep(1);
        alert('✅ Profile saved! You can now start tracking.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Profile save error:', response.status, errorData);
        alert(`Failed to save profile: ${errorData.detail || 'Server error. Please try again.'}`);
      }
    } catch (error) {
      console.error('Profile save error:', error);
      alert(`Network error: ${error.message || 'Please check your connection and try again.'}`);
    }
  };
  
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', maxHeight: '85vh', overflow: 'auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ marginBottom: '8px', fontSize: '20px' }}>📋 Quick Profile Setup</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
            Just the basics — you can start tracking right after
          </p>
        </div>
        
        {/* Progress - 3 steps */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              style={{
                width: '60px',
                height: '4px',
                borderRadius: '2px',
                background: step <= onboardingStep ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255,255,255,0.1)'
              }}
            />
          ))}
        </div>
        
        {/* STEP 1: Goal Selection + Units */}
        {onboardingStep === 1 && (
          <div>
            <h3 style={{ textAlign: 'center', marginBottom: '16px', fontSize: '16px' }}>🎯 What is your goal?</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {[
                { value: 'lose', icon: '⬇️', label: 'Lose Weight', desc: 'Calorie deficit for fat loss' },
                { value: 'maintain', icon: '⚖️', label: 'Maintain Weight', desc: 'Stay at current weight' },
                { value: 'gain', icon: '💪', label: 'Build Muscle', desc: 'Calorie surplus for gains' }
              ].map((goal) => (
                <button
                  key={goal.value}
                  onClick={() => setOnboardingData({ ...onboardingData, goal: goal.value })}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: onboardingData.goal === goal.value ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                    background: onboardingData.goal === goal.value ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{goal.icon}</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>{goal.label}</p>
                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>{goal.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Unit Preference - Show when goal selected and not maintain */}
            {onboardingData.goal && onboardingData.goal !== 'maintain' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>Units</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setOnboardingData({ ...onboardingData, useImperial: false })}
                    style={{ 
                      flex: 1, 
                      padding: '10px',
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      border: !onboardingData.useImperial ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                      background: !onboardingData.useImperial ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                      fontWeight: '500',
                      fontSize: '13px'
                    }}
                  >
                    Metric (kg)
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnboardingData({ ...onboardingData, useImperial: true })}
                    style={{ 
                      flex: 1, 
                      padding: '10px',
                      borderRadius: '8px', 
                      cursor: 'pointer',
                      border: onboardingData.useImperial ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                      background: onboardingData.useImperial ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                      fontWeight: '500',
                      fontSize: '13px'
                    }}
                  >
                    Imperial (lbs)
                  </button>
                </div>
              </div>
            )}
            
            {/* Target Weight (optional) */}
            {onboardingData.goal && onboardingData.goal !== 'maintain' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                  Target Weight <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>(optional)</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    id="onboarding-goal-weight-input"
                    key="onboarding-goal-weight-input"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    value={onboardingData.goalWeight}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setOnboardingData(prev => ({ ...prev, goalWeight: val }));
                    }}
                    placeholder={onboardingData.useImperial ? "165" : "75"}
                    className="search-input"
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {onboardingData.useImperial ? 'lbs' : 'kg'}
                  </span>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => setOnboardingStep(2)} 
                disabled={!onboardingData.goal}
                className="btn-primary" 
                style={{ 
                  flex: 1, 
                  padding: '14px 20px', 
                  minHeight: '50px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}
        
        {/* STEP 2: Profile Details */}
        {onboardingStep === 2 && (
          <div>
            <h3 style={{ textAlign: 'center', marginBottom: '16px', fontSize: '16px' }}>📊 Your Details</h3>
            
            {/* Unit Preference */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>Units</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setOnboardingData({ ...onboardingData, useImperial: false })}
                  style={{ 
                    flex: 1, 
                    padding: '10px',
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    border: !onboardingData.useImperial ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                    background: !onboardingData.useImperial ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                    fontWeight: '500',
                    fontSize: '13px'
                  }}
                >
                  Metric (kg, cm)
                </button>
                <button
                  type="button"
                  onClick={() => setOnboardingData({ ...onboardingData, useImperial: true })}
                  style={{ 
                    flex: 1, 
                    padding: '10px',
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    border: onboardingData.useImperial ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                    background: onboardingData.useImperial ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                    fontWeight: '500',
                    fontSize: '13px'
                  }}
                >
                  Imperial (lbs, ft)
                </button>
              </div>
            </div>
            
            {/* Gender & Age */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>Sex</label>
                  <select
                    value={onboardingData.gender}
                    onChange={(e) => setOnboardingData({ ...onboardingData, gender: e.target.value })}
                    className="search-input"
                    style={{ width: '100%', padding: '10px' }}
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>Age</label>
                  <input
                    id="onboarding-age-input"
                    key="onboarding-age-input"
                    type="text" 
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={onboardingData.age}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      setOnboardingData(prev => ({ ...prev, age: val }));
                    }}
                    placeholder="28"
                    className="search-input"
                    style={{ width: '100%', padding: '10px' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Height */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                Height
              </label>
              {onboardingData.useImperial ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <select
                      value={onboardingData.heightFt || ''}
                      onChange={(e) => {
                        const ft = e.target.value;
                        const inches = onboardingData.heightIn || 0;
                        const totalCm = Math.round((parseInt(ft || 0) * 30.48) + (parseInt(inches) * 2.54));
                        setOnboardingData({ ...onboardingData, heightFt: ft, height: String(totalCm) });
                      }}
                      className="search-input"
                      style={{ width: '100%', padding: '10px' }}
                    >
                      <option value="">Feet</option>
                      {feetOptions.map(ft => (
                        <option key={ft} value={ft}>{ft} ft</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <select
                      value={onboardingData.heightIn || ''}
                      onChange={(e) => {
                        const inches = e.target.value;
                        const ft = onboardingData.heightFt || 0;
                        const totalCm = Math.round((parseInt(ft) * 30.48) + (parseInt(inches || 0) * 2.54));
                        setOnboardingData({ ...onboardingData, heightIn: inches, height: String(totalCm) });
                      }}
                      className="search-input"
                      style={{ width: '100%', padding: '10px' }}
                    >
                      <option value="">Inches</option>
                      {inchesOptions.map(inch => (
                        <option key={inch} value={inch}>{inch} in</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input 
                    id="onboarding-height-input"
                    key="onboarding-height-input"
                    type="text" 
                    inputMode="decimal"
                    pattern="[0-9]*"
                    value={onboardingData.height} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      setOnboardingData(prev => ({ ...prev, height: val }));
                    }}
                    placeholder="170" 
                    className="search-input" 
                    style={{ flex: 1, padding: '10px' }}
                  />
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>cm</span>
                </div>
              )}
            </div>
            
            {/* Weight */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                Weight
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input 
                  id="onboarding-weight-input"
                  key="onboarding-weight-input"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  value={onboardingData.useImperial ? (onboardingData.weightLbs || '') : onboardingData.weight}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.]/g, '');
                    if (onboardingData.useImperial) {
                      const kg = Math.round(parseFloat(val || 0) * 0.453592);
                      setOnboardingData(prev => ({ ...prev, weightLbs: val, weight: String(kg) }));
                    } else {
                      setOnboardingData(prev => ({ ...prev, weight: val }));
                    }
                  }}
                  placeholder={onboardingData.useImperial ? "155" : "70"}
                  className="search-input" 
                  style={{ flex: 1, padding: '10px' }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {onboardingData.useImperial ? 'lbs' : 'kg'}
                </span>
              </div>
            </div>
            
            {/* Activity Level */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>Activity Level</label>
              <select
                value={onboardingData.activityLevel}
                onChange={(e) => setOnboardingData({ ...onboardingData, activityLevel: e.target.value })}
                className="search-input"
                style={{ width: '100%', padding: '10px' }}
              >
                <option value="">Select activity level</option>
                <option value="sedentary">Sedentary (desk job, little exercise)</option>
                <option value="light">Light (light exercise 1-3 days/week)</option>
                <option value="moderate">Moderate (exercise 3-5 days/week)</option>
                <option value="active">Active (exercise 6-7 days/week)</option>
                <option value="very_active">Very Active (intense daily exercise)</option>
              </select>
            </div>
            
            {/* Dietary Preference */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                Dietary Preference <span style={{ color: 'var(--text-secondary)', fontWeight: '400' }}>(optional)</span>
              </label>
              <select
                data-testid="onboarding-dietary-preference"
                value={onboardingData.dietaryPreference}
                onChange={(e) => setOnboardingData({ ...onboardingData, dietaryPreference: e.target.value })}
                className="search-input"
                style={{ width: '100%', padding: '10px' }}
              >
                <option value="">No preference</option>
                <option value="omnivore">Omnivore</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="vegan">Vegan</option>
                <option value="pescatarian">Pescatarian</option>
                <option value="keto">Keto</option>
                <option value="paleo">Paleo</option>
                <option value="halal">Halal</option>
                <option value="kosher">Kosher</option>
                <option value="gluten_free">Gluten Free</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setOnboardingStep(1)} className="btn-secondary" style={{ 
                flex: 1, 
                padding: '14px 16px', 
                minHeight: '50px',
                fontSize: '14px',
                borderRadius: '10px'
              }}>
                ← Back
              </button>
              <button 
                onClick={() => setOnboardingStep(3)} 
                disabled={!onboardingData.gender || !onboardingData.age || !onboardingData.height || !onboardingData.weight || !onboardingData.activityLevel}
                className="btn-primary" 
                style={{ 
                  flex: 2, 
                  padding: '14px 20px', 
                  minHeight: '50px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}
        
        {/* STEP 3: Quick Confirmation */}
        {onboardingStep === 3 && (
          <div>
            <h3 style={{ textAlign: 'center', marginBottom: '12px', fontSize: '16px' }}>✨ You're All Set!</h3>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
              Your profile is ready. Start tracking meals and let MIQ personalize your nutrition journey.
            </p>
            
            <div style={{ 
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid rgba(102, 126, 234, 0.2)'
            }}>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 8px 0' }}>
                <strong style={{ color: '#667eea' }}>💡 Pro Tip:</strong>
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                Have medical conditions or food allergies? Set up <strong>SmartGoals</strong> from the home screen to unlock personalized meal recommendations that respect your health profile.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setOnboardingStep(2)} className="btn-secondary" style={{ 
                flex: 1, 
                padding: '14px 16px', 
                minHeight: '50px',
                fontSize: '14px',
                borderRadius: '10px'
              }}>
                ← Back
              </button>
              <button 
                onClick={handleComplete}
                className="btn-primary" 
                style={{ 
                  flex: 2, 
                  padding: '14px 20px', 
                  minHeight: '50px',
                  fontSize: '15px',
                  fontWeight: '600',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
              >
                🎉 Start Tracking
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;
